import { z } from 'zod';
import { COMPANY_LOGO_MAX_BYTES, COMPANY_LOGO_MIME_TYPES, INVOICE_DUE_DAYS_MAX, LEGAL_FORMS, QUOTE_VALIDITY_DAYS_MAX, isHexColor, findLegalForm, requiresCommercialRegister, } from '../constants/index.js';
import { countryCodeSchema, emptyToNull, idSchema, isoDateTimeSchema } from './common.js';
export const legalFormSchema = z.enum(LEGAL_FORMS);
export const internationalLegalFormFields = {
    legalFormCode: z
        .string()
        .refine((code) => code === 'OTHER' || findLegalForm(code) !== undefined, 'international.invalidLegalForm')
        .nullish(),
    legalFormName: z.string().trim().max(200).nullish(),
    legalFormJurisdiction: z
        .string()
        .regex(/^[A-Z]{2}(?:-[A-Z0-9]{1,3})?$/u)
        .nullish(),
};
export const hexColorSchema = z
    .string()
    .trim()
    .refine(isHexColor, 'Erwartet wird ein Hex-Farbcode wie #1D4ED8.');
const DATA_URL_PATTERN = /^data:(image\/png|image\/jpeg|image\/svg\+xml);base64,([A-Za-z0-9+/=]+)$/u;
/** Logo als Data-URL: MIME-Typ und Rohgröße werden vor der Speicherung geprüft. */
export const companyLogoSchema = z
    .string()
    .trim()
    .max(Math.ceil((COMPANY_LOGO_MAX_BYTES * 4) / 3) + 100)
    .superRefine((value, ctx) => {
    const match = DATA_URL_PATTERN.exec(value);
    if (match === null) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Erlaubt sind ${COMPANY_LOGO_MIME_TYPES.join(', ')} als Data-URL.`,
        });
        return;
    }
    const base64 = match[2] ?? '';
    const padding = base64.endsWith('==') ? 2 : base64.endsWith('=') ? 1 : 0;
    const bytes = Math.floor((base64.length * 3) / 4) - padding;
    if (bytes > COMPANY_LOGO_MAX_BYTES) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Die Datei überschreitet ${String(Math.round(COMPANY_LOGO_MAX_BYTES / 1024))} KB.`,
        });
    }
});
export const companyProfileSchema = z.object({
    id: idSchema,
    companyName: z.string(),
    legalForm: legalFormSchema,
    ...internationalLegalFormFields,
    ownerName: z.string().nullable(),
    street: z.string().nullable(),
    zip: z.string().nullable(),
    city: z.string().nullable(),
    country: z.string(),
    phone: z.string().nullable(),
    email: z.string().nullable(),
    website: z.string().nullable(),
    vatId: z.string().nullable(),
    taxNumber: z.string().nullable(),
    commercialRegister: z.string().nullable(),
    bankName: z.string().nullable(),
    iban: z.string().nullable(),
    bic: z.string().nullable(),
    logoUrl: z.string().nullable(),
    primaryColor: z.string(),
    defaultInvoiceDueDays: z.number().int().min(0),
    defaultQuoteValidityDays: z.number().int().min(1),
    defaultEmailSender: z.string().nullable(),
    updatedAt: isoDateTimeSchema,
});
const optionalText = (max) => emptyToNull(z.string().trim().max(max));
export const companyProfileUpdateSchema = z
    .object({
    companyName: z.string().trim().min(2).max(160),
    legalForm: legalFormSchema,
    ...internationalLegalFormFields,
    ownerName: optionalText(160),
    street: optionalText(160),
    zip: optionalText(16),
    city: optionalText(120),
    country: countryCodeSchema.default('DE'),
    phone: optionalText(40),
    email: emptyToNull(z.string().trim().toLowerCase().email().max(200)),
    website: emptyToNull(z.string().trim().url().max(200)),
    vatId: emptyToNull(z
        .string()
        .trim()
        .toUpperCase()
        .regex(/^[A-Z]{2}[A-Z0-9]{2,13}$/u, 'Erwartet wird eine USt-IdNr. wie DE123456789.')),
    taxNumber: optionalText(24),
    commercialRegister: optionalText(80),
    bankName: optionalText(120),
    iban: emptyToNull(z
        .string()
        .trim()
        .toUpperCase()
        .transform((value) => value.replace(/\s+/gu, ''))
        .pipe(z.string().regex(/^[A-Z]{2}[0-9]{2}[A-Z0-9]{10,30}$/u, 'IBAN ist ungültig.'))),
    bic: emptyToNull(z
        .string()
        .trim()
        .toUpperCase()
        .regex(/^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/u, 'BIC ist ungültig.')),
    logoUrl: companyLogoSchema.nullable().default(null),
    primaryColor: hexColorSchema,
    defaultInvoiceDueDays: z.coerce.number().int().min(0).max(INVOICE_DUE_DAYS_MAX),
    defaultQuoteValidityDays: z.coerce.number().int().min(1).max(QUOTE_VALIDITY_DAYS_MAX),
    defaultEmailSender: emptyToNull(z.string().trim().toLowerCase().email().max(200)),
})
    .superRefine((value, ctx) => {
    if (value.legalFormCode === 'OTHER' && !value.legalFormName?.trim()) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['legalFormName'],
            message: 'international.otherRequired',
        });
    }
    if (requiresCommercialRegister(value.legalForm, value.legalFormJurisdiction ?? value.country, value.legalFormCode) &&
        value.commercialRegister === null) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['commercialRegister'],
            message: 'Diese Rechtsform erfordert einen Handelsregistereintrag.',
        });
    }
});
//# sourceMappingURL=company.js.map